import { BUTTON_SIZE, DSBottomSheet, DSButton, DSModal } from '@repo/design-system/index'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { useCallback, useEffect, useState } from 'react'
import { useEventTracking } from '../helpers/gtmHelper'
import { ITutor } from '../model/tutor.interface'
import { useUserCreditDuration } from '../modules/apis/student/credit'
import {
  useApplyLessons,
  useAvailableCoupon,
  useAvailableCouponBundles,
  useAvailableTimesForTutor,
  useHoldTimeslot,
} from '../modules/apis/student/lesson/schedule/queries'
import {
  AvailableCoupon,
  AvailableCouponBundlesResponse,
  IApplyInfo,
  ISchedule,
  ITimeslot,
} from '../modules/apis/student/lesson/schedule/types'
import { APP_SCREEN_NAME } from '../modules/hooks/common/appRouterVars'
import useAppRouter from '../modules/hooks/common/useAppRouter'
import useIsWebview from '../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../modules/hooks/webview/common/useReturnFullUrl'
import { n_common, n_module } from '../modules/i18n/strings/auto/wording'
import useTranslation from '../modules/i18n/useTranslation'
import { upcomingsQueryApiV5 } from '../modules/react-query/queryApis'
import urls from '../modules/urls'
import { formatDateWithDuration } from '../modules/utils'
import { useSidePopup } from './basic/popup/SidePopup'
import PackageSelectModal from './new-portal/lessons/schedules/PackageSelectModal'
import ScheduleModalBody from './new-portal/lessons/schedules/ScheduleModalBody'
import TimeSlotUnavailableModal from './new-portal/lessons/schedules/TimeSlotUnavailableModal'
import { TUTOR_PROFILE_TAB } from './student/new-portal/tutors/TutorBody'
import { initialApplyInfoData } from './student/new-portal/tutors/TutorScheduleLesson'

const Content = ({ tutor, handleTutorProfile, timeslot, duration }) => {
  const { t, isLocaleKO } = useTranslation()
  return (
    <>
      <div className="flex">
        <img src={tutor.image_url} className="w-[96px] h-[96px] rounded-[20px]" />
        <div className="ml-[16px]">
          <div className="Title1-20Bd">{tutor.first_name}</div>
          <div className="Body2-14Md">{tutor.major}</div>
          <div className="Body2-14Md text-gray-500">{tutor.school}</div>
          {tutor.primary_keyword && <div className="Body2-14Bd mt-[8px] text-gray-500">#{tutor.primary_keyword}</div>}
        </div>
      </div>
      <div className="mt-[32px] py-[20px] px-[16px] bg-gray-50 rounded-[12px]">
        <div className="Body2-14Md text-gray-600">{t(n_module.one_click.lesson_caption)}</div>
        <div className="mt-[4px] Title2-18Bd"> {formatDateWithDuration(timeslot, duration.value, isLocaleKO)}</div>
        <div
          className="cursor-pointer mt-[20px] rounded-[8px] bg-gray-100 flex items-center justify-center p-[8px] Body2-14Md"
          onClick={handleTutorProfile}
        >
          {t(n_module.one_click.lesson_btn)}
        </div>
      </div>
    </>
  )
}

interface RecommendTutorSchedulePopupProps {
  tutor: ITutor
  showTutorTime: boolean
  setShowTutorTime: (show: boolean) => void
  setResult: (result: any) => void
}

const RecommendTutorSchedulePopup = ({
  tutor,
  showTutorTime,
  setShowTutorTime,
  setResult,
}: RecommendTutorSchedulePopupProps) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const getFullUrl = useReturnFullUrl()
  const sidePopup = useSidePopup()
  const [availableTimes, setAvailableTimes] = useState([])
  const [selectedDay, setSelectedDay] = useState<Date>()
  const [timeslot, setTimeslot] = useState<ITimeslot>(null)
  const [couponBundlesData, setCouponBundlesData] = useState(null)
  const [showErrorMessage, setShowErrorMessage] = useState('')
  const [showErrorMessagePopup, setShowErrorMessagePopup] = useState(false)
  const [showPackageSelectModal, setShowPackageSelectModal] = useState(false)
  const [showNoAvailablePackage, setShowNoAvailablePackage] = useState(false)
  const [showNoAvailableCreditsMessage, setShowNoAvailableCreditsMessage] = useState('')
  const [showNoAvailableCreditsPopup, setShowNoAvailableCreditsPopup] = useState(false)
  const { sendClickEvent, sendPageViewEvent } = useEventTracking()
  const [schedules, setSchedules] = useState<ISchedule[]>([])
  const [applyInfo, setApplyInfo] = useState<IApplyInfo>(initialApplyInfoData)
  const queryClient = useQueryClient()
  const resetTimeslot = () => {
    const applyData = {
      ...applyInfo,
      selectedTimeslot: null,
    }
    setApplyInfo(applyData)
  }
  const durationOptionList = [
    {
      label: t(n_common.label_20min),
      value: 20,
      disabled: false,
    },
    {
      label: t(n_common.label_40min),
      value: 40,
      disabled: false,
    },
  ]

  const [duration, setDuration] = useState(durationOptionList[0])
  useUserCreditDuration({
    onSuccess: (data) => {
      setDuration(durationOptionList.find((durationOption) => durationOption.value == data))
    },
  })

  const { isFetching } = useAvailableTimesForTutor(tutor.id, duration.value, {
    onSuccess: (data) => {
      setAvailableTimes(data)
      if (data.length > 0) {
        const date = new Date()
        let found = false
        let firstAvailableTime = null
        let times = []
        const enabledDates = data?.filter(
          (classtime) => classtime?.timeslots?.filter((time) => time.is_opened)?.length > 0
        )
        if (enabledDates.length > 0) {
          while (!found) {
            const selectedDate = format(date, 'yyyy-MM-dd')
            times = data.find((item) => item.date == selectedDate)?.timeslots
            const availableTimes = times?.filter((item) => item.is_opened)

            if (availableTimes && availableTimes.length >= 1) {
              firstAvailableTime = availableTimes[0]
              found = true
            } else {
              date.setDate(date.getDate() + 1)
            }
          }
          if (firstAvailableTime) {
            setTimeslot(firstAvailableTime)
          }
          setApplyInfo({
            ...applyInfo,
            duration: duration.value,
          })
        }
      } else {
        setSelectedDay(new Date())
      }
    },
  })

  const handleTutorProfile = () => {
    sendClickEvent({ button_name: 'different_time' })
    router.push(urls.student.newPortal.tutors.detail.as(tutor.id, TUTOR_PROFILE_TAB.SCHEDULE), {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: {
        url: getFullUrl(urls.webview.tutors.detail.as(tutor.id, TUTOR_PROFILE_TAB.SCHEDULE)),
      },
    })
  }
  const { mutate: holdTimeslot } = useHoldTimeslot(applyInfo.selectedTimeslot?.id)
  const { mutate: apply } = useApplyLessons()

  const closeSidePopup = () => {
    sidePopup.close('lesson-popup')
  }

  const handleDeleteConfirm = () => {
    closeSidePopup()
    setSchedules([])
    setApplyInfo({
      ...applyInfo,
      selectedTimeslot: null,
      selectedCoupon: null,
    })
  }

  const showSidePopup = () => {
    sidePopup.show({
      id: 'lesson-popup',
      overlayClose: true,
      border: true,
      bgWhite: false,
      maxW: 405,
      hFull: true,
      gapFromTop: 0,
      noTopSide: true,
      body: (
        <ScheduleModalBody
          setSchedules={setSchedules}
          schedules={schedules}
          handleDelete={handleDeleteConfirm}
          handleSubmit={handleSubmit}
        />
      ),
    })
  }

  useAvailableCouponBundles(applyInfo.selectedTimeslot?.classtime_id, applyInfo?.duration, {
    onSuccess: (data: AvailableCouponBundlesResponse) => {
      setCouponBundlesData(data)
      let list = []
      const lastMinutesCoupons = data.last_minute_coupons
      const urgentCoupons = data.urgent_coupons
      list = list.concat(data.packages)
      if (lastMinutesCoupons?.is_available) {
        list.push(lastMinutesCoupons)
      }
      if (urgentCoupons?.is_available) {
        list.push(urgentCoupons)
      }
      const availablePackages = list.filter((data) => data.is_available)
      if (applyInfo.selectedTimeslot.id) {
        if (availablePackages.length > 0) {
          holdTimeslot(
            { duration: applyInfo.duration },
            {
              onSuccess: (data) => {
                if (data.error) {
                  setShowErrorMessage(data.error)
                  setShowErrorMessagePopup(true)
                } else {
                  if (availablePackages.length > 1) {
                    setShowPackageSelectModal(true)
                  } else {
                    const availablePackage = availablePackages[0]
                    if (availablePackage.coupon_bundle_id) {
                      setApplyInfo({
                        ...applyInfo,
                        selectedCoupon: {
                          ...applyInfo.selectedCoupon,
                          couponBundleId: availablePackage.coupon_bundle_id,
                          isWeeklyLessonPackage: availablePackage.is_weekly_lesson_package,
                        },
                      })
                    } else {
                      setApplyInfo({
                        ...applyInfo,
                        selectedCoupon: {
                          ...applyInfo.selectedCoupon,
                          couponTypeId: availablePackage.coupon_type_id,
                          isWeeklyLessonPackage: availablePackage.is_weekly_lesson_package,
                        },
                      })
                    }
                  }
                }
              },
            }
          )
        } else {
          setApplyInfo({
            ...applyInfo,
            selectedTimeslot: null,
          })
          setShowNoAvailablePackage(true)
        }
      }
    },
    onError: (error) => {
      if (error?.response?.data?.error) {
        setShowNoAvailableCreditsMessage(error?.response?.data?.error)
        setShowNoAvailableCreditsPopup(true)
      }
    },
  })

  useAvailableCoupon(
    applyInfo,
    schedules.map((schedule) => schedule.couponId),
    {
      onSuccess: (data: AvailableCoupon) => {
        const newData = {
          ...applyInfo,
          tutor: tutor,
          couponId: data.id,
          matchingAfter: 1,
        }
        setSchedules([newData])
      },
    }
  )

  useEffect(() => {
    if (showTutorTime) {
      sendPageViewEvent({ page_name: 'book_offer_details' })
    }
  }, [showTutorTime])

  useEffect(() => {
    if (schedules.length > 0) {
      setShowTutorTime(false)
      showSidePopup()
    }
  }, [schedules])

  const handleApply = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const date = new Date(
      Date.UTC(timeslot?.year, timeslot?.month - 1, timeslot?.day, timeslot?.hour, timeslot?.minute)
    )
    const kstDate = utcToZonedTime(date, 'Asia/Seoul')
    sendClickEvent({
      button_name: 'complete_booking',
      location_detail: 'book_suggestion_details_modal',
      object_context: format(kstDate, 'yyyy-MM-dd HH:mm'),
    })
    setApplyInfo({
      ...applyInfo,
      selectedTimeslot: timeslot,
    })
  }

  const [isTimeSlotUnavailableModalOpen, setIsTimeSlotUnavailableModalOpen] = useState(false)
  const handleSubmit = useCallback(
    (skip = false) => {
      if (skip || !schedules.find((schedule) => schedule.selectedTimeslot.is_today)) {
        const payload = schedules.map((schedule, i) => {
          return schedule.selectedCoupon.couponBundleId
            ? {
                duration: schedule.duration,
                matching_after: schedule.matchingAfter,
                timeslot_id: schedule.selectedTimeslot.id,
                coupon_bundle_id: schedule.selectedCoupon.couponBundleId,
              }
            : {
                duration: schedule.duration,
                matching_after: schedule.matchingAfter,
                timeslot_id: schedule.selectedTimeslot.id,
                coupon_type_id: schedule.selectedCoupon.couponTypeId,
              }
        })
        apply(
          { application_objects: payload },
          {
            onSuccess: (data) => {
              const isSuccess = data.results.every((result) => result.success)
              if (isSuccess) {
                closeSidePopup()
                setResult({
                  data: data,
                  schedules: schedules,
                })
              } else {
                setIsTimeSlotUnavailableModalOpen(true)
              }

              queryClient.invalidateQueries(upcomingsQueryApiV5.getQueryKey())
            },
          }
        )
      }
    },
    [schedules]
  )
  const iswebview = useIsWebview()

  return (
    <>
      {iswebview ? (
        <DSBottomSheet
          id="TextOnlySMTwoButtonHorizontalPopup"
          isOpen={showTutorTime}
          setIsOpen={setShowTutorTime}
          closeButton={true}
          onClose={() => sendClickEvent({ button_name: 'close', location_detail: 'book_suggestion_details_modal' })}
        >
          <DSModal.Content className="mt-[16px] p-[20px]">
            <Content tutor={tutor} handleTutorProfile={handleTutorProfile} timeslot={timeslot} duration={duration} />
            <DSButton size={BUTTON_SIZE.XL} onClick={handleApply}>
              <DSButton.Label>{t(n_module.one_click.btn_2)}</DSButton.Label>
            </DSButton>
          </DSModal.Content>
        </DSBottomSheet>
      ) : (
        <DSModal
          id="TextOnlySMTwoButtonHorizontalPopup"
          isOpen={showTutorTime}
          setIsOpen={setShowTutorTime}
          size={'lg'}
          closeButton={true}
        >
          <DSModal.Title>
            <div></div>
          </DSModal.Title>
          <DSModal.Content className="mt-[16px]">
            <Content tutor={tutor} handleTutorProfile={handleTutorProfile} timeslot={timeslot} duration={duration} />
          </DSModal.Content>
          <DSModal.ButtonContainer direction="horizontal">
            <DSButton size={BUTTON_SIZE.LG} onClick={handleApply}>
              <DSButton.Label>{t(n_module.one_click.btn_2)}</DSButton.Label>
            </DSButton>
          </DSModal.ButtonContainer>
        </DSModal>
      )}

      {couponBundlesData && (
        <PackageSelectModal
          showPackageSelectModal={showPackageSelectModal}
          setShowPackageSelectModal={setShowPackageSelectModal}
          applyInfo={applyInfo}
          setApplyInfo={setApplyInfo}
          data={couponBundlesData}
          onClose={() => {
            resetTimeslot()
            setShowPackageSelectModal(false)
          }}
        />
      )}

      <TimeSlotUnavailableModal
        isOpen={isTimeSlotUnavailableModalOpen}
        onOpen={() => setIsTimeSlotUnavailableModalOpen((prev) => !prev)}
      />
    </>
  )
}

export default RecommendTutorSchedulePopup
